import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { LeadSubmissionResponse } from "@shared-core/data/api-types";
import type { LeadSource } from "@shared-core/data/marketing-types";
import { paths } from "@shared-core/routing/routes";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Badge } from "../app/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const TABLE_COLUMN_COUNT = 6;

/** The enquirer's name as the form captured it. `last_name` is optional on a
 * submission, so a single-name entry renders as that name rather than a
 * trailing space. */
function submissionName(submission: LeadSubmissionResponse): string {
  return [submission.first_name, submission.last_name]
    .filter(Boolean)
    .join(" ");
}

/**
 * The individual leads captured by one landing page — the drill-in behind a
 * source's submission count.
 *
 * This is the surface that answers "did my form submission actually arrive?",
 * which previously had no home in the UI at all: the backend has exposed
 * the source's submissions since the module landed (GET /leads/submissions
 * ?lead_source_id={id} now), but nothing
 * rendered it, so confirming a capture meant going looking for the ticket it
 * happened to open.
 */
export function LandingPageSubmissionsPage() {
  const { leadSourceId = "" } = useParams<{ leadSourceId: string }>();
  const dataAdapter = useDataAdapter();

  const [source, setSource] = useState<LeadSource | null>(null);
  const [submissions, setSubmissions] = useState<
    LeadSubmissionResponse[] | null
  >(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const [sourceRes, submissionsRes] = await Promise.all([
          dataAdapter.getLeadSource(leadSourceId),
          dataAdapter.listLeadSubmissions(leadSourceId, { page }),
        ]);
        if (cancelled) return;
        setSource(sourceRes);
        setSubmissions(submissionsRes.items);
        setTotal(submissionsRes.total);
        setTotalPages(submissionsRes.totalPages);
      } catch (e) {
        if (cancelled) return;
        console.error("marketing.landing_page_submissions.load.failed", e);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, leadSourceId, page]);

  return (
    <div className="space-y-6">
      <MarketingNav active="landing-pages" />

      <div>
        <Link
          to={paths.marketingLandingPages()}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="size-3.5" />
          Landing pages
        </Link>
        <h1 className="text-2xl mb-1">
          {source ? `Leads from ${source.name}` : "Leads"}
        </h1>
        <p className="text-sm text-muted-foreground">
          Every form submission this page has captured, newest first. Each one
          also opens a ticket for the matching sales rep.
          {!loading && !error && total > 0 && (
            <>
              {" "}
              <span className="text-foreground">
                {submissions && submissions.length < total
                  ? `Showing ${submissions.length} of ${total}.`
                  : `${total} in total.`}
              </span>
            </>
          )}
        </p>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">Loading leads…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load leads. Please try again.
        </div>
      )}

      {!loading && !error && submissions && (
        <div className="rounded-lg border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Matched to</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMN_COUNT}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No leads captured yet. If you've just submitted the form and
                    nothing is showing, the page may not be sending to One
                    correctly.
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell className="font-medium text-foreground">
                      {submission.ticket_id ? (
                        <Link
                          to={paths.ticketDetail({ id: submission.ticket_id })}
                          className="text-primary hover:underline"
                        >
                          {submissionName(submission)}
                        </Link>
                      ) : (
                        // No ticket means routing never completed for this
                        // submission. Rendering a dead link would be worse
                        // than plain text: the enquiry is real and visible
                        // here, it simply has nothing to open.
                        submissionName(submission)
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {submission.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {submission.phone ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {submission.company_name ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTimeOrDash(submission.submitted_at)}
                    </TableCell>
                    <TableCell>
                      {submission.resolved_customer_id ? (
                        <Badge variant="success">
                          {submission.resolved_customer_id}
                        </Badge>
                      ) : submission.contact_created === true ? (
                        <Badge variant="neutral">New name</Badge>
                      ) : submission.contact_created === false ? (
                        <Badge variant="secondary">Known contact</Badge>
                      ) : (
                        // Null means we genuinely do not know: the submission
                        // predates the field, or routing never completed.
                        // Guessing here is what made this column claim we knew
                        // people we had never heard of.
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
